import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import { apiPost, apiGet, clearAuth, getToken, getRefreshToken, refreshAccessToken, setRole, setTokens } from "../api/client";
import { endpoints } from "../api/endpoints";
import { persistAuth } from "../utils/authPersist";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const token = getToken();
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      if (decoded.exp && decoded.exp * 1000 < Date.now()) return null;
      return decoded.employee || decoded;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setUser(null);
      return;
    }
    let cancelled = false;
    try {
      const decoded = jwtDecode(token);
      const expired = decoded.exp && decoded.exp * 1000 <= Date.now();
      if (!expired) {
        // Valid token — make sure the user is populated even if the initial
        // useState ran before restoreAuth() repopulated storage.
        setUser(decoded.employee || decoded);
        return;
      }
      // Access token expired (e.g. opening the app the next morning, past the
      // 8h TTL). Try the still-valid refresh token BEFORE logging out — the old
      // code called clearAuth() here, which wiped the refresh token too and
      // forced a fresh login every time.
      if (getRefreshToken()) {
        refreshAccessToken()
          .then((newToken) => {
            if (cancelled || !newToken) return;
            const d = jwtDecode(newToken);
            setUser(d.employee || d);
          })
          .catch(() => { if (!cancelled) { clearAuth(); setUser(null); } });
      } else {
        clearAuth();
        setUser(null);
      }
    } catch {
      clearAuth();
      setUser(null);
    }
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!user?.employeeid) return undefined;
    let cancelled = false;
    apiGet(endpoints.selfService.access)
      .then((data) => {
        if (cancelled || !data?.orgAccess) return;
        setUser((prev) => (prev ? { ...prev, orgAccess: data.orgAccess } : prev));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.employeeid]);

  const login = async (email, password, { remember = true } = {}) => {
    const data = await apiPost(endpoints.auth.login, { email, password });
    setTokens({ token: data.token, refreshToken: data.refreshToken }, { remember });
    if (data.employee?.role) setRole(data.employee.role);
    if (data.employee?.employeeid) sessionStorage.setItem("employeeid", data.employee.employeeid);
    if (data.employee?.name) sessionStorage.setItem("name", data.employee.name);
    if (remember) persistAuth();
    setUser(data.employee);
    return data;
  };

  const logout = async () => {
    // Record the logout server-side for the audit log (best-effort), then clear.
    try { await apiPost("/api/auth/logout", {}); } catch (_) { /* ignore */ }
    clearAuth();
    setUser(null);
    window.location.href = "/login";
  };

  const hasRole = useCallback((...roles) => {
    if (!user) return false;
    const role = (user.role || "").toLowerCase();
    return roles.flat().map((r) => r.toLowerCase()).includes(role);
  }, [user]);

  const hasAccess = useCallback((kind) => {
    if (!user) return false;
    const role = (user.role || "").toLowerCase();
    const isAdminHr = role === "admin" || role === "hr";
    const oa = user.orgAccess;
    if (kind === "critical") return oa ? !!oa.canAccessCritical : isAdminHr;
    if (kind === "approver") return oa ? !!oa.canApproveTeam : isAdminHr || role === "manager";
    return false;
  }, [user]);

  const updateUser = (patch) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const value = useMemo(
    () => ({ user, login, logout, hasRole, hasAccess, updateUser, isAuthenticated: !!user }),
    [user, hasRole, hasAccess]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
